import { mediaCache, resolveAuthenticatedMediaUrl } from "./media";

// ── Voice message playback ───────────────────────────────────
// One <audio> element plays every voice message, for as long as the page
// lives. The chat throws its timeline away on every room switch, and after a
// Turbo visit away from /chat and back it starts over with a new UI
// (bryght_chat.js). With an element per message, the one playing was orphaned
// the moment the user left the conversation: it kept playing — on purpose, as
// in WhatsApp — but the redrawn message showed it as stopped, and no button
// reached it any more.
//
// Voice messages render through bindVoiceMessage below and only *show* the
// player's state: every change is pushed to each message in the document that
// plays the same file, so a message drawn later picks up a playback already
// running. The lock screen and Now Playing controls go through the Media
// Session API to the same player.
//
// The element stays detached from the document. A media element removed from
// the document is paused by the browser, and Turbo replaces the whole body on
// every visit — attached, it would stop the moment the user left the chat.

// Lock screen skip buttons, in seconds.
const SKIP_SECONDS = 15;
// The recorder measures the length with a timer that can undershoot the audio
// by a tick, so only a clear overrun counts as the end — never cut the last
// word off.
const OVERRUN_TOLERANCE = 0.3;

class VoicePlayer {
  constructor() {
    this.audio = null;
    // { key, mxcUrl, mimeType, duration, title, artist, album, artwork }
    this.track = null;
    // The file of the current track is on the element.
    this.ready = false;
    // A play asked for while the file was still loading; started once it is in.
    this.wantsToPlay = false;
    // A seek asked for before the file was in.
    this.pendingSeek = null;
    // Set once a message played through, so the next play starts at the top.
    this.finished = false;
    // Where playback was at the last timeupdate, or null right after a seek.
    this.lastTime = null;
    this.loadToken = 0;
    this.ownsMediaSession = false;
  }

  isCurrent(key) {
    return !!this.track && this.track.key === key;
  }

  isPlaying() {
    return this.wantsToPlay || (!!this.audio && !this.audio.paused);
  }

  duration() {
    if (this.track?.duration > 0) return this.track.duration;
    const fileDuration = this.audio?.duration;
    return Number.isFinite(fileDuration) && fileDuration > 0 ? fileDuration : 0;
  }

  position() {
    if (this.finished) return 0;
    if (this.pendingSeek !== null) return this.pendingSeek;
    return this.audio?.currentTime || 0;
  }

  stateFor(key) {
    if (!this.isCurrent(key)) return { active: false, playing: false, position: 0, duration: 0 };
    return { active: true, playing: this.isPlaying(), position: this.position(), duration: this.duration() };
  }

  toggle(track) {
    if (this.isCurrent(track.key) && this.isPlaying()) {
      this.pause();
    } else {
      this.play(track);
    }
  }

  play(track = this.track) {
    if (!track) return;
    this.load(track);

    if (this.finished) {
      this.finished = false;
      this.seekTo(0);
    }
    this.wantsToPlay = true;
    if (this.ready) this.startPlayback();
    this.notify();
  }

  pause() {
    this.wantsToPlay = false;
    this.audio?.pause();
    this.notify();
  }

  stop() {
    this.pause();
    this.seekTo(0);
  }

  // Back to no message at all: the file let go, the lock screen cleared. For a
  // download that failed, so the next tap tries again, and for a chat retired
  // for good (see retireUnlessChatOnPage).
  reset() {
    const previousKey = this.track?.key;
    // A download still on its way must not attach its file afterwards.
    this.loadToken++;
    this.track = null;
    this.ready = false;
    this.wantsToPlay = false;
    this.pendingSeek = null;
    this.finished = false;
    this.lastTime = null;
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute("src");
      this.audio.load();
    }
    this.releaseMediaSession();
    this.notify(previousKey);
  }

  // `ratio` of the message's length; tapping the waveform also starts playback.
  seekTrack(track, ratio) {
    this.load(track);
    const total = this.duration();
    // Length unknown until the file is in: nothing to seek within yet.
    if (!total) {
      if (!this.isPlaying()) this.play(track);
      return;
    }

    this.finished = false;
    this.seekTo(ratio * total);
    if (!this.isPlaying()) this.play(track);
  }

  seekTo(time) {
    const total = this.duration();
    const target = Math.max(0, total ? Math.min(time, total) : time);
    this.lastTime = null;
    this.finished = false;

    if (!this.ready) {
      this.pendingSeek = target;
    } else {
      try {
        this.audio.currentTime = target;
      } catch (_) {
        // Not seekable yet: start there once it is.
        this.pendingSeek = target;
      }
    }
    this.notify();
    this.syncMediaSession();
  }

  // Makes `track` the current one; a no-op when it already is.
  load(track) {
    if (this.isCurrent(track.key)) {
      // Fresher display info (a name resolved since the first render).
      this.track = { ...this.track, ...track };
      return;
    }

    const previousKey = this.track?.key;
    const audio = this.element();
    audio.pause();

    this.track = track;
    this.ready = false;
    this.wantsToPlay = false;
    this.pendingSeek = null;
    this.finished = false;
    this.lastTime = null;
    const token = ++this.loadToken;

    // Synchronously when the file is at hand, so play() runs inside the tap:
    // WKWebView only lets a user gesture start audio.
    const cached = mediaCache.get(track.mxcUrl);
    if (cached) {
      this.attach(cached);
    } else {
      audio.removeAttribute("src");
      audio.load();
      resolveAuthenticatedMediaUrl(track.mxcUrl, "audio", track.mimeType).then((url) => {
        if (token !== this.loadToken) return;
        // Kept as the current track, the message would never be fetched again
        // (load() is a no-op for it), and the next tap would show it playing
        // with nothing to play. Let go of it: the next tap starts over.
        if (!url) {
          this.reset();
          return;
        }
        this.attach(url);
        if (this.wantsToPlay) this.startPlayback();
        this.notify();
      });
    }

    this.notify(previousKey);
  }

  attach(url) {
    const audio = this.element();
    audio.src = url;
    this.ready = true;
    if (this.pendingSeek !== null) {
      try {
        audio.currentTime = this.pendingSeek;
        this.pendingSeek = null;
      } catch (_) { /* applied on loadedmetadata */ }
    }
  }

  startPlayback() {
    const playing = this.audio.play();
    playing?.catch((err) => {
      // A newer source replaced this one; its own play() follows.
      if (err?.name === "AbortError") return;
      console.warn("[BryghtChat] Voice message playback failed:", err);
      this.wantsToPlay = false;
      this.notify();
    });
  }

  finish() {
    this.wantsToPlay = false;
    this.audio.pause();
    try { this.audio.currentTime = 0; } catch (_) { /* not seekable */ }
    this.finished = true;
    this.lastTime = null;
    this.notify();
    this.syncMediaSession();
  }

  element() {
    if (this.audio) return this.audio;

    const audio = document.createElement("audio");
    audio.preload = "auto";
    audio.loop = false;
    this.audio = audio;

    audio.addEventListener("play", () => {
      this.wantsToPlay = false;
      this.finished = false;
      this.claimMediaSession();
      this.notify();
    });
    // Leaves wantsToPlay alone: switching to a file that is still loading
    // pauses the previous one, and that late event must not cancel the play
    // the user just asked for. Every pause that is meant (the button, the lock
    // screen, the end) goes through pause() or finish().
    audio.addEventListener("pause", () => {
      this.notify();
      this.syncMediaSession();
    });
    audio.addEventListener("ended", () => this.finish());
    audio.addEventListener("seeking", () => { this.lastTime = null; });
    audio.addEventListener("seeked", () => {
      this.lastTime = null;
      this.syncMediaSession();
    });
    audio.addEventListener("loadedmetadata", () => {
      if (this.pendingSeek !== null) {
        try { audio.currentTime = this.pendingSeek; } catch (_) { /* keeps the start */ }
        this.pendingSeek = null;
      }
      this.notify();
      this.syncMediaSession();
    });
    audio.addEventListener("durationchange", () => this.notify());
    audio.addEventListener("timeupdate", () => this.handleTimeUpdate());
    audio.addEventListener("error", () => {
      if (!audio.getAttribute("src")) return;
      console.warn("[BryghtChat] Voice message could not be played:", audio.error);
      this.wantsToPlay = false;
      this.notify();
    });

    // Another player on the page (a lesson's audio, a video) takes over:
    // the voice message stops, and the lock screen belongs to the other one.
    document.addEventListener("play", (event) => {
      if (event.target === audio || !(event.target instanceof HTMLMediaElement)) return;
      if (this.isPlaying()) this.pause();
      this.releaseMediaSession();
    }, true);

    return audio;
  }

  handleTimeUpdate() {
    const audio = this.audio;
    const now = audio.currentTime;

    if (!audio.paused && !audio.seeking && this.lastTime !== null) {
      // A MediaRecorder file without a length header may run on past the
      // recorded length or start over from the top, which read as the message
      // looping. Only a jump back from the end counts as that: every seek
      // resets lastTime, so going back by hand — in the chat or on the lock
      // screen — never ends the message.
      const known = this.track?.duration || 0;
      const total = this.duration();
      const overran = known > 0 && now >= known + OVERRUN_TOLERANCE;
      const wrapped = now + 1 < this.lastTime && (!total || this.lastTime >= total - 2);
      if (overran || wrapped) {
        this.finish();
        return;
      }
    }

    this.lastTime = audio.seeking ? null : now;
    this.notify();
  }

  // Pushes the state to every message in the document that plays `key` (the
  // current track by default). Messages that are not in the document — the
  // timeline of a room left, a Turbo snapshot — are not reached and need not be.
  notify(...extraKeys) {
    const keys = new Set([this.track?.key, ...extraKeys].filter(Boolean));
    if (keys.size === 0) return;

    document.querySelectorAll(".bryght-voice-message").forEach((el) => {
      if (keys.has(el._voiceKey)) el._renderVoiceState?.(this.stateFor(el._voiceKey));
    });
  }

  // ── Media Session (lock screen, Control Center, headset buttons) ──
  claimMediaSession() {
    const session = navigator.mediaSession;
    if (!session || !this.track) return;

    if (typeof MediaMetadata !== "undefined") {
      const { title, artist, album, artwork } = this.track;
      session.metadata = new MediaMetadata({
        title: title || "",
        artist: artist || "",
        album: album || "",
        artwork: artwork ? [{ src: artwork }] : [],
      });
    }

    if (!this.ownsMediaSession) {
      const position = () => this.position();
      this.setActionHandlers({
        play: () => this.play(),
        pause: () => this.pause(),
        stop: () => this.stop(),
        seekto: (details) => this.seekTo(details.seekTime),
        seekbackward: (details) => this.seekTo(position() - (details.seekOffset || SKIP_SECONDS)),
        seekforward: (details) => this.seekTo(position() + (details.seekOffset || SKIP_SECONDS)),
        previoustrack: null,
        nexttrack: null,
      });
      this.ownsMediaSession = true;
    }

    this.syncMediaSession();
  }

  // Clears what the voice message left on the session, too: the session is
  // shared, and the next player on the page (a lesson's AudioPlayer sets only
  // metadata) would otherwise show the message's sender, length and state. Runs
  // before that player's own play handler on a takeover (a capture listener),
  // so its metadata is not cleared away.
  releaseMediaSession() {
    if (!this.ownsMediaSession) return;
    this.ownsMediaSession = false;
    this.setActionHandlers({ play: null, pause: null, stop: null, seekto: null, seekbackward: null, seekforward: null });

    const session = navigator.mediaSession;
    try {
      session.metadata = null;
      session.playbackState = "none";
      session.setPositionState?.();
    } catch (_) { /* rejected by the browser; nothing left to control anyway */ }
  }

  setActionHandlers(handlers) {
    Object.entries(handlers).forEach(([action, handler]) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch (_) { /* action not supported by this browser */ }
    });
  }

  // The lock screen extrapolates the position itself; it needs the anchor
  // whenever playback starts, stops or jumps.
  syncMediaSession() {
    const session = navigator.mediaSession;
    if (!session || !this.ownsMediaSession || !this.audio) return;

    session.playbackState = this.audio.paused ? "paused" : "playing";

    const duration = this.duration();
    if (!duration || typeof session.setPositionState !== "function") return;
    try {
      session.setPositionState({
        duration,
        position: Math.min(this.position(), duration),
        playbackRate: this.audio.playbackRate || 1,
      });
    } catch (_) { /* rejected by the browser; the controls still work */ }
  }
}

// The length of an audio file, for a message whose event carries none. The
// probe element is let go once it answered; it never plays.
export function probeAudioDuration(url, onDuration) {
  const probe = document.createElement("audio");
  probe.preload = "metadata";
  probe.addEventListener("loadedmetadata", () => {
    if (Number.isFinite(probe.duration) && probe.duration > 0) onDuration(probe.duration);
    probe.removeAttribute("src");
    probe.load();
  }, { once: true });
  probe.src = url;
}

// A page with neither the chat nor the drawer it lives in has no chat to come
// back to: the one after a logout, which is a Turbo visit (this JS context and
// the detached element survive it), or one that carries no chat at all. A
// private message must not play on there, with its sender on the lock screen
// and no button in the page to stop it. Between pages that carry the drawer it
// keeps playing, as before.
function retireUnlessChatOnPage(player) {
  if (document.getElementById("bryght-chat") || document.getElementById("global-chat-drawer")) return;
  player.reset();
}

function createVoicePlayer() {
  const player = new VoicePlayer();
  document.addEventListener("turbo:load", () => retireUnlessChatOnPage(player));
  return player;
}

// One per page, however often the chat module is evaluated.
export const voicePlayer = window.__bryghtVoicePlayer || (window.__bryghtVoicePlayer = createVoicePlayer());
